import { ref } from 'vue';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

interface ItemResult {
  codigo: string;
  nome?: string;
  tipo: string;
  sucesso: boolean;
  erro?: string;
}

interface JobResult {
  totalProcessados: number;
  totalSucesso: number;
  totalFalha: number;
  resultados: ItemResult[];
}

interface JobResponse {
  status: number; // 1 = Processando, 2 = Sucesso, 3 = Falha
  resultadoJsonDocument?: JobResult;
}

interface CienciaEncerramentoLegalAi2Request {
  compromissoCodigos: string[];
  observacao?: string;
}

export function useCienciaEncerramentoLegalAi2() {
  const status = ref<'idle' | 'loading' | 'success' | 'partial' | 'error'>('idle');
  const jobId = ref<string | null>(null);
  const result = ref<JobResult | null>(null);
  const errorMessage = ref<string | null>(null);
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const pollJob = async (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get<JobResponse>(`${API_URL}/get-job`, {
            params: { jobId: id },
            getAccessToken: true,
            timeout: 30000,
          } as any);

          const job = response.data;

          if (job.status === 1) {
            return;
          }

          stopPolling();

          if (job.status === 2) {
            result.value = job.resultadoJsonDocument || null;

            if (result.value && result.value.totalFalha > 0 && result.value.totalSucesso > 0) {
              status.value = 'partial';
            } else {
              status.value = 'success';
            }
            resolve();
          } else if (job.status === 3) {
            result.value = job.resultadoJsonDocument || null;
            status.value = 'error';
            errorMessage.value = result.value?.resultados
              ?.filter(r => !r.sucesso)
              ?.map(r => r.erro)
              ?.join(', ') || 'Erro ao registrar ciência';
            resolve();
          }
        } catch (error: any) {
          stopPolling();
          status.value = 'error';
          errorMessage.value = error?.message || 'Erro ao verificar status do job';
          reject(error);
        }
      }, 2000);
    });
  };

  const submit = async (request: CienciaEncerramentoLegalAi2Request): Promise<void> => {
    status.value = 'loading';
    jobId.value = null;
    result.value = null;
    errorMessage.value = null;

    try {
      const response = await axios.post(
        `${API_URL}/encerramento-legal-ai-2/ciencia-encerramento-legal-ai-2`,
        request,
        {
          getAccessToken: true,
        } as any,
      );

      if (!response.data?.jobId) {
        throw new Error('JobId não retornado pelo servidor');
      }

      jobId.value = response.data.jobId;

      await pollJob(jobId.value!);
    } catch (error: any) {
      status.value = 'error';
      errorMessage.value =
        error.response?.status === 400
          ? error.response?.data?.errorMessage ?? 'Tivemos um problema inesperado'
          : 'Tivemos um problema inesperado';
    }
  };

  const reset = () => {
    stopPolling();
    status.value = 'idle';
    jobId.value = null;
    result.value = null;
    errorMessage.value = null;
  };

  const setLoading = () => {
    status.value = 'loading';
  };

  return {
    status,
    jobId,
    result,
    errorMessage,
    submit,
    reset,
    stopPolling,
    setLoading,
  };
}
